import * as Immutable from 'immutable';
import type { $PropertyType } from 'utility-types';

type InternalState = {
  id: string;
  dataAdapterId: string;
  key: string;
  values: Array<string>;
  ttl: number;
  ttl_unit: string;
  expire_after: string;
};

export type MongoDBDataAdapterEntryJSON = {
  id: string;
  data_adapter_id: string;
  key: string;
  values: Array<string>;
  ttl: number;
  ttl_unit: string;
  expire_after: string;
};

export default class MongoDBDataAdapterEntry {
  _value: InternalState;

  // eslint-disable-next-line no-undef
  constructor(
    id: string,
    dataAdapterId: string,
    key: string,
    values: Array<string>,
    ttl: number,
    ttl_unit: string,
    expire_after: string,
  ) {
    this._value = { id, dataAdapterId, key, values, ttl, ttl_unit, expire_after };
  }

  get id() {
    return this._value.id;
  }

  get dataAdapterId() {
    return this._value.dataAdapterId;
  }

  get key() {
    return this._value.key;
  }

  get values() {
    return this._value.values;
  }

  get ttl() {
    return this._value.ttl;
  }

  get ttlUnit() {
    return this._value.ttl_unit;
  }

  get expire_after() {
    return this._value.expire_after;
  }

  toBuilder() {
    // eslint-disable-next-line no-use-before-define
    return new Builder(Immutable.Map(this._value as { [key: string]: any }));
  }

  // eslint-disable-next-line no-undef
  static create(
    id: string,
    dataAdapterId: string,
    key: string,
    value: Array<string>,
    ttl: number,
    ttl_unit: string,
    expire_after: string,
  ) {
    return new MongoDBDataAdapterEntry(id, dataAdapterId, key, value, ttl, ttl_unit, expire_after);
  }

  static builder() {
    // eslint-disable-next-line no-use-before-define
    return new Builder();
  }

  toJSON() {
    const { id, dataAdapterId, key, values, ttl, ttl_unit, expire_after } = this._value;

    return {
      id,
      data_adapter_id: dataAdapterId,
      key,
      values,
      ttl,
      ttl_unit,
      expire_after,
    };
  }

  static fromJSON(_value: MongoDBDataAdapterEntryJSON) {
    const { id, data_adapter_id: dataAdapterId, key, values, ttl, ttl_unit, expire_after } = _value;

    return MongoDBDataAdapterEntry.create(id, dataAdapterId, key, values, ttl, ttl_unit, expire_after);
  }
}

type BuilderState = Immutable.Map<string, any>;

class Builder {
  _value: BuilderState;

  constructor(value: BuilderState = Immutable.Map()) {
    this._value = value;
  }

  // eslint-disable-next-line no-undef
  id(value: $PropertyType<InternalState, 'id'>) {
    return new Builder(this._value.set('id', value));
  }

  // eslint-disable-next-line no-undef
  dataAdapterId(value: $PropertyType<InternalState, 'dataAdapterId'>) {
    return new Builder(this._value.set('dataAdapterId', value));
  }

  // eslint-disable-next-line no-undef
  key(value: $PropertyType<InternalState, 'key'>) {
    return new Builder(this._value.set('key', value));
  }

  // eslint-disable-next-line no-undef
  values(values: $PropertyType<InternalState, 'values'>) {
    return new Builder(this._value.set('values', values));
  }

  ttl(value: $PropertyType<InternalState, 'ttl'>) {
    return new Builder(this._value.set('ttl', value));
  }

  ttl_unit(value: $PropertyType<InternalState, 'ttl_unit'>) {
    return new Builder(this._value.set('ttl_unit', value));
  }

  expire_after(value: $PropertyType<InternalState, 'expire_after'>) {
    return new Builder(this._value.set('expire_after', value));
  }

  build() {
    const { id, dataAdapterId, key, values, ttl, ttl_unit, expire_after } = this._value.toObject();

    return new MongoDBDataAdapterEntry(id, dataAdapterId, key, values, ttl, ttl_unit, expire_after);
  }
}
